import _debounce from 'lodash/debounce'
import { Logger, CubePresenter, ScopeSlot, FlipIntent, NOOP_VOID, Presenter } from 'cube'

import { MainPresenter } from '../main/main.presenter'

import { AnaliseBulasKey } from './analise-bulas.key'
import { AnaliseBulasScope, CompareTableScope } from './analise-bulas.scope'

import queryBulas, { QueryBulasResponse, type QueryBulasRequest, type FabricanteInfo, type GradeReacaoPorFabricanteRow } from './service/query-bulas-site'
import queryDominios, { type BulaDominioRequest, type BulaDominioResponse, type BulaDominioIten } from './service/query-dominios'

import DominioSelectPresenter from './dominio-select.presenter'

const LOG = Logger.get('AnaliseBulasPresenter')

export class AnaliseBulasPresenter extends CubePresenter<MainPresenter, AnaliseBulasScope> {
    // :: Fields
    protected parentSlot: ScopeSlot = NOOP_VOID
    protected rowIdGen = 1

    protected readonly fabricanteSelect!: DominioSelectPresenter
    protected readonly medicamentoSelect!: DominioSelectPresenter
    protected readonly principiosAtivosSelect!: DominioSelectPresenter
    protected readonly reacoesSelect!: DominioSelectPresenter

    protected readonly fabricantesManager = new FabricanteManager()

    // :: Constructor
    constructor(app: MainPresenter) {
        super(app, new AnaliseBulasScope())

        const filtroCtrl = new Presenter(this, this.scope.filtro)
        this.scope.filtro.onLimpar = filtroCtrl.action(this.onLimpar, this)
        this.scope.filtro.onConsultar = _debounce(filtroCtrl.action(this.onConsultar, this), 100)
        this.scope.filtro.update = filtroCtrl.update

        this.fabricantesManager.assignTableScope(this.scope.table)

        this.fabricanteSelect = new DominioSelectPresenter(filtroCtrl, this.scope.filtro.fabricantes,
            'fabricantes', this.fabricantesManager.fetch)
        this.fabricantesManager.assignSelectPresenter(this.fabricanteSelect)

        this.medicamentoSelect = new DominioSelectPresenter(filtroCtrl, this.scope.filtro.medicamentos,
            'medicamentos', queryDominios.medicamentos)
        this.principiosAtivosSelect = new DominioSelectPresenter(filtroCtrl, this.scope.filtro.principiosAtivos,
            'principiosAtivos', queryDominios.principiosAtivos)
        this.reacoesSelect = new DominioSelectPresenter(filtroCtrl, this.scope.filtro.reacoes,
            'reacoes', queryDominios.reacoes)

        this.scope.table.update = this.update
    }

    // :: Cube lifecycle

    override async applyParameters(intent: FlipIntent, initialization: boolean) {
        const key = new AnaliseBulasKey(this.app, intent)
        this.parentSlot = key.parentSlot
        this.parentSlot(this.scope)

        // Puxa configuracao do Intent
        const changed = await this.updateFilterStateFromIntent(key, initialization)

        if (changed && key.temDados) {
            await this.onConsultar()
        }

        return true
    }

    override publishParameters(intent: FlipIntent): void {
        const key = new AnaliseBulasKey(this.app, intent)
        key.temDados = this.scope.table.reacaoPorFabricanteData !== null
        key.principioAtivos = Util.keysToParam(this.scope.filtro.principiosAtivos.value)
        key.fabricantes = Util.keysToParam(this.scope.filtro.fabricantes.value)
        key.medicamentos = Util.keysToParam(this.scope.filtro.medicamentos.value)
        key.reacoes = Util.keysToParam(this.scope.filtro.reacoes.value)
    }

    // :: Listeners

    async onLimpar() {
        await this.fabricanteSelect.onLimpar()
        await this.medicamentoSelect.onLimpar()
        await this.principiosAtivosSelect.onLimpar()
        await this.reacoesSelect.onLimpar()

        this.fabricantesManager.clearData()

        this.scope.table.qtdeFabricatesAnalisados = 0
        this.scope.table.qtdeCoberturasCompletas = 0
        this.scope.table.qtdeCoberturaParciais = 0
        this.scope.table.qtdeNaoDocumentadas = 0
        this.scope.table.reacaoPorFabricanteData = null
    }

    async onConsultar() {
        const queryRequest: QueryBulasRequest = {
            //fabricantes: this.fabricanteSelect.selectedCodes,
            medicamentos: this.medicamentoSelect.selectedCodes,
            principios: this.principiosAtivosSelect.selectedCodes,
            reacoes: this.reacoesSelect.selectedCodes,
        }

        if (LOG.isDebugEnabled()) {
            LOG.debug(`queryBulas(${JSON.stringify(queryRequest)})`)
        }

        this.scope.loading = true
        const data = await queryBulas(queryRequest).finally(() => this.scope.loading = false)

        await this.fabricantesManager.updateData(data)

        this.scope.table.qtdeFabricatesAnalisados = data.qtdeFabricates
        this.scope.table.qtdeCoberturasCompletas = data.qtdeCoberturasCompletas
        this.scope.table.qtdeCoberturaParciais = data.qtdeCoberturaParciais
        this.scope.table.qtdeNaoDocumentadas = data.qtdeNaoDocumentadas
        this.scope.table.reacaoPorFabricanteData = data.grade
    }

    // :: Update ViewState from Intent

    async updateFilterStateFromIntent(key: AnaliseBulasKey, initialization: boolean) {
        const me = this
        const tmpKey = new AnaliseBulasKey(this.app, new FlipIntent(key.place))

        let pendingTasks = [] as Promise<void>[]

        let changed = initialization
        changed = updatePrincipiosAtivos() || changed
        changed = updateFabricantes() || changed
        changed = updateMedicamentos() || changed
        changed = updateReacoes() || changed

        if (pendingTasks.length > 0) {
            await Promise.all(pendingTasks)
        }

        return changed

        function updatePrincipiosAtivos() {
            tmpKey.principioAtivos = Util.keysToParam(me.scope.filtro.principiosAtivos.value)
            const ov = tmpKey.principioAtivos
            const nv = key.principioAtivos
            if (ov !== nv) {
                pendingTasks.push(me.principiosAtivosSelect.setSelectedCodes(Util.paramToKeys(nv)))
                return true
            }
            return false
        }

        function updateFabricantes() {
            tmpKey.fabricantes = Util.keysToParam(me.scope.filtro.fabricantes.value)
            const ov = tmpKey.fabricantes
            const nv = key.fabricantes
            if (ov !== nv) {
                pendingTasks.push(me.fabricanteSelect.setSelectedCodes(Util.paramToKeys(nv)))
                return true
            }
            return false
        }

        function updateMedicamentos() {
            tmpKey.medicamentos = Util.keysToParam(me.scope.filtro.medicamentos.value)
            const ov = tmpKey.medicamentos
            const nv = key.medicamentos
            if (ov !== nv) {
                pendingTasks.push(me.medicamentoSelect.setSelectedCodes(Util.paramToKeys(nv)))
                return true
            }
            return false
        }

        function updateReacoes() {
            tmpKey.reacoes = Util.keysToParam(me.scope.filtro.reacoes.value)
            const ov = tmpKey.reacoes
            const nv = key.reacoes
            if (ov !== nv) {
                pendingTasks.push(me.reacoesSelect.setSelectedCodes(Util.paramToKeys(nv)))
                return true
            }
            return false
        }
    }

}

export default AnaliseBulasPresenter

const Util = {
    keysToParam(keys: string[] | null | undefined) {
        return Array.isArray(keys) && keys.length > 0 ? keys.join(',') : undefined
    },

    paramToKeys(v: string | undefined): string[] {
        if (!v) {
            return []
        }
        return v.split(',')
    },

    removerAcentos(str: string): string {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
}

type FranicanteBulaOption = BulaDominioIten & {
    descricaoUpper: string
}

class FabricanteManager {

    readonly options: FranicanteBulaOption[] = []

    private tableScope!: CompareTableScope
    private selectPresenter!: DominioSelectPresenter
    private empresasOriginal: FabricanteInfo[] = []
    private rowsOriginal: GradeReacaoPorFabricanteRow[] = []
    private updating = false

    readonly fetch = async (rq: BulaDominioRequest): Promise<BulaDominioResponse> => {
        if (this.options.length === 0) {
            return queryDominios.fabricantes(rq)
        }

        if (!rq.text) {
            return { duration: 0, itens: this.options }
        }

        const searchText = Util.removerAcentos(rq.text).toUpperCase()
        const filtedOptions: FranicanteBulaOption[] = []
        for(const option of this.options) {
            if (option.descricaoUpper.includes(searchText)) {
                filtedOptions.push(option)
            }
        }

        return { duration: 0, itens: filtedOptions }
    }

    assignTableScope(tableScope: CompareTableScope) {
        this.tableScope = tableScope
    }

    assignSelectPresenter(presenter: DominioSelectPresenter) {
        this.selectPresenter = presenter
        this.selectPresenter.onCheckChangedListener = codes => this.onCheckChanged(codes)
    }

    clearData() {
        this.updating = true
        try {
            this.options.length = 0
            this.empresasOriginal = []
            this.rowsOriginal = []
            this.selectPresenter.onLimpar()
        } finally {
            this.updating = false
        }
    }

    async updateData(data: QueryBulasResponse) {
        this.updating = true
        try {
            this.doUpdateData(data)
        } finally {
            this.updating = false
        }
    }

    private async doUpdateData(data: QueryBulasResponse) {
        this.empresasOriginal = data.grade.fabricantes
        this.rowsOriginal = data.grade.rows

        this.options.length = 0
        for (const empresa of data.grade.fabricantes) {
            this.options.push({
                id: empresa.id,
                descricao: empresa.nome,
                descricaoUpper: Util.removerAcentos(empresa.nome.toUpperCase())
            })
        }

        const localeOptions: Intl.CollatorOptions = { sensitivity: 'base' }
        this.options.sort((a, b) => a.descricao.localeCompare(b.descricao, undefined, localeOptions))

        this.selectPresenter.forceContent(this.options)

        const selectedCodeSet = new Set(this.selectPresenter.selectedCodes)
        if (selectedCodeSet.size > 5) {
            selectedCodeSet.clear()
        }

        if (selectedCodeSet.size === 0) {
            for (let i = 0; i < 5; i++) {
                const option = this.options[i]
                if (option) {
                    selectedCodeSet.add(option.id)
                } else {
                    break
                }
            }
        }

        if (selectedCodeSet.size > 0) {
            await this.selectPresenter.setSelectedExistingCodes([...selectedCodeSet])
        }

        const [newEmpresas, newRows] = this.trimData(selectedCodeSet)
        data.grade.fabricantes = newEmpresas
        data.grade.rows = newRows
    }

    private trimData(selectedCodeSet: Set<string>): [FabricanteInfo[], GradeReacaoPorFabricanteRow[]] {
        const existingColSet = new Set<number>()
        const newEmpresas: FabricanteInfo[] = []
        for (let i = 0; i < this.empresasOriginal.length; i++) {
            const empresa = this.empresasOriginal[i]
            if (selectedCodeSet.has(empresa.id)) {
                newEmpresas.push(empresa)
                existingColSet.add(i)
            }
        }

        const newRows: GradeReacaoPorFabricanteRow[] = []
        for (const row of this.rowsOriginal) {
            const newRow: GradeReacaoPorFabricanteRow = {
                id: row.id,
                qtdeCasos: row.qtdeCasos,
                reacao: row.reacao,
                medicamentos: []
            }
            for (let col = 0; col < row.medicamentos.length; col++) {
                if (existingColSet.has(col)) {
                    newRow.medicamentos.push(row.medicamentos[col])
                }
            }
            newRows.push(newRow)
        }

        return [newEmpresas, newRows]
    }

    private onCheckChanged(codes: string[]) {
        if (this.updating) {
            return
        }

        if (this.tableScope.reacaoPorFabricanteData) {
            const [newEmpresas, newRows] = this.trimData(new Set(codes))
            this.tableScope.reacaoPorFabricanteData.fabricantes = newEmpresas
            this.tableScope.reacaoPorFabricanteData.rows = newRows
            this.tableScope.update()
        }
    }

}
